import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DetailPanelData } from "../Common/DetailPanel";
import { renderWeight } from '../../Utils/weight_utils';
import { Button } from '@mui/material';
import CreateEpaAlertDialog from './CreateEpaAlertDialog';
import Routes from '../../routes';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Grey3 } from '../../color_utils';
import Enums from '../../enums';
import {FormatDate, isDesktop } from '../../utils';


const CommonAssetDetails = ({
    data: {
        isCylinder = null,
        assetType = null,
        cylinderStatusName = null,
        name = '',
        id = null,
        assetSubType = {},
        locationName = '',
        location = null,
        tagNumber = null,
        serialNumber = null,
        acquisitionDate = null,
        disbursementDate = null,
        installDate = null,
        manufacturerName = '',
        modelNumber = '',
        manufactureDate = '',
        modelYear = null,
        poNumber = null,
        assetStatusName = '',
        leakRate = null,
        locationOnSite = '',
        lifeTime = null,
        chargeCapacityLBS = '',
        materialTypeName = '',
        calendarYearLeakRate = null,
        materialClass = null,
        maxGasCapacity = '',
        aimActLeakRate = null,
    } = {},
    regulatedAssetTypes = [],
}) => {
    const initialFieldValue = {
        value: null,
        hasError: false,
    };

    const isFireSurpression = assetType === Enums.AssetTypes.HVACR;
    const isControl = assetType === Enums.AssetTypes.ControlsControl;
    const [openModal, setOpenModal]=useState(false)
    const [isLoading, setIsLoading]=useState(false)
    const [isDeleteLoading, setIsDeleteLoading]=useState(false)
    const [leakRatePercent, setLeakRatePercent] = useState(0)
    const [leakRateId, setLeakRateId] = useState(null)
    const [leakRateValue, setLeakRateValue] = useState(initialFieldValue);

    const handleOpen = () => setOpenModal(true)
    const handleClose = () => setOpenModal(false)

    const fetchLeakRates = async () => {
        const response = await fetch(Routes.Asset.GetLeakRateExceed(id));
        if (response.status === 200){
            const data = await response.json();
            if (data) {
                setLeakRatePercent(data.userDefinedVar1);
                setLeakRateId(data.id);
            }
        } else {
            setLeakRatePercent(0);
            setLeakRateId(null);
        }
    }

    useEffect(()=>{
        fetchLeakRates();
    },[openModal])

    const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
    };

    const onModalClose = () => {
        handleClose();
        setLeakRateValue(initialFieldValue);
    };

    const handleOnChange = (value) => {
        setLeakRateValue({
            hasError: false,
            value: value,
        });
    };

    // leak rate from 5 - 100 %
    const leakRateOption = Array.from({ length: 96 }, (_, index) => ({
        id: index + 5,
        name: index + 5,
    }));
    
    const hasValue = leakRatePercent > 0 && leakRateId != null ? true : false;

    const onSave = async () => {
        try {
            setIsLoading(true);
            let isFormValid = true;
            if (!leakRateValue.value) {
                setLeakRateValue({ ...leakRateValue, hasError: true });
            }

            if (!isFormValid) {
                setIsLoading(false);
                return;
            }

            const body = {
                Id: leakRateId,
                assetId: id,
                LeakRatePercent: leakRateValue.value,
            };
            
            const endpoint = hasValue ? Routes.Asset.UpdateLeakRateExceed() : Routes.Asset.CreateLeakRateExceed();

            const response = await fetch(endpoint, {
                method: hasValue ? "PATCH" : "POST",
                headers,
                body: JSON.stringify(body),
            });

            if (response.status === 200) {
                fetchLeakRates()
                setIsLoading(false);
                onModalClose();
            }
        } catch (e) {
            console.log(`fail save ${e}`);
            setIsLoading(false);
        }
    };

    const onDelete = async () => {
        try {
            setIsDeleteLoading(true);
            const response = await fetch(Routes.Asset.DeleteLeakRateExceed(leakRateId), {
                method: "PATCH",
                headers,
            });

            if (response.status === 200) {
                fetchLeakRates()
                onModalClose();
                setIsDeleteLoading(false);
            }
        } catch {
            setIsDeleteLoading(false);
        } finally {
            setIsDeleteLoading(false);
        }
    };

    const headerTitle = hasValue ? "Update the percentage of the alert or delete the alert" :  "Create an alert when leak rate exceeds:";
    const disabled = leakRateValue.value === null;

    return (
        <>
            <DetailPanelData title="ID" data={id} />
            {!isFireSurpression && (
                <DetailPanelData id='StatusHeader' title="STATUS" data={assetStatusName} />
            )}
            <DetailPanelData id='AssetSubType' title="SUBTYPE" data={assetSubType.name}  />
            <DetailPanelData title="LOCATION" data={locationName} link={"/Location/"} linkId={location} />
            <DetailPanelData id='leakRate' title={"EPA METHOD LEAK RATE"} data={`${leakRate ? leakRate : 0}%`}
                createAlertButton={
                    isDesktop && (
                        <>
                            <FiberManualRecordIcon style={{ fontSize:'5px', marginLeft:'5px', color: Grey3 }} fontSize='small' />
                            <Button
                                onClick={handleOpen}
                                className="subheading subheadingUnderLine"
                                style={{ color: 'grey', textTransform: leakRatePercent == 0 ? 'capitalize' :"none" }}
                                variant='text'
                            >
                                {hasValue ? `✓ Alert created: ${leakRatePercent}%`:"Create alert"}
                            </Button>
                        </>
                    )
                }
            />
            <DetailPanelData id='MaterialTypeHeader' title="MATERIAL TYPE" data={materialTypeName}  />
            {(isCylinder === false && !isControl) && (
                <DetailPanelData id='ChargeCapacityHeader' title="CHARGE CAPACITY" data={renderWeight(chargeCapacityLBS? chargeCapacityLBS: 0)}  />
            )}
            {isControl && (
                <DetailPanelData id='MaxGasCapacityHeader' title="MAX GAS CAPACITY" data={renderWeight(maxGasCapacity)}  />
            )}
            <DetailPanelData id='leakRate' title="CALENDAR YEAR LEAK RATE" data={`${calendarYearLeakRate.toFixed(1)} %`}  />
            {regulatedAssetTypes.some(r => r.name === "EPA AIM Act" && r.isSelected) && (
                <DetailPanelData id='aimActLeakRate' title="EPA AIM ACT ROLLING METHOD LEAK RATE" data={`${aimActLeakRate != null ? aimActLeakRate : 0} %`} />
            )}
            <DetailPanelData id='materialClass' title="MATERIAL CLASS" data={`${materialClass}`}  />
            <DetailPanelData title="NAME" data={name}   />
            {!isFireSurpression && (
                <>
                    <DetailPanelData id='TagNumberHeader' title="TAG NUMBER" data={tagNumber}  />
                    <DetailPanelData id='SerialNumberHeader' title="SERIAL NUMBER" data={serialNumber}  />
                    <DetailPanelData id='AcquisitionDateHeader' title="ACQUISITION DATE" data={FormatDate(acquisitionDate)}  />
                    <DetailPanelData id='DisbursementDateHeader' title="DISBURSEMENT DATE" data={FormatDate(disbursementDate)}  />
                    {isCylinder === false && (
                        <DetailPanelData id='InstallDateHeader' title="INSTALL DATE" data={FormatDate(installDate)}  />
                    )}
                    {isCylinder === true && (
                        <DetailPanelData id='cylinderStatus' title="CYLINDER STATUS" data={cylinderStatusName}  />   
                    )}
                    <DetailPanelData id='SublocationHeader' title="SUBLOCATION" data={locationOnSite}  />
                    <DetailPanelData id='ManufacturerNameHeader' title="MANUFACTURER NAME" data={manufacturerName}  />
                    <DetailPanelData id='ModelNumberHeader' title="MODEL NUMBER" data={modelNumber}  />
                    <DetailPanelData id='ManufactureDateHeader' title="MANUFACTURER DATE" data={FormatDate(manufactureDate)}  />
                    <DetailPanelData id='ModelYearHeader' title="MODEL YEAR" data={modelYear}  />
                    <DetailPanelData id='PONumberHeader' title="PO NUMBER" data={poNumber}  />
                    <DetailPanelData id='ExpectedLifeHeader' title="EXPECTED LIFE (YEARS)" data={lifeTime}  />
                </>
            )}
            <CreateEpaAlertDialog 
                leakRatePercent={leakRatePercent} 
                open={openModal} 
                isDeleteLoading={isDeleteLoading}
                onSave={onSave}
                onDelete={onDelete}
                isLoading={isLoading}
                leakRate={leakRateValue}
                options={leakRateOption}
                handleOnChange={handleOnChange}
                headerName={headerTitle}
                buttonTitle={leakRatePercent > 0 ? "UPDATE ALERT" : "CREATE ALERT"}
                initialFieldValue={initialFieldValue}
                disabled={disabled}
                headerWidth={hasValue ? '80%' : '70%'}
                onModalClose={onModalClose} />
        </>
    );
}

CommonAssetDetails.propTypes = {
    data: PropTypes.object.isRequired
};

export default CommonAssetDetails;
